/**
 * The one place the browser talks to the backend.
 *
 * The API is GraphQL, so every screen asks for exactly the fields it renders. Errors arrive as
 * `{ errors: [{ message, extensions: { code } }] }`; they are unwrapped here into a typed `ApiError` rather than every
 * caller reaching into `errors[0].extensions.code` and getting it slightly wrong.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }

  get isRateLimited(): boolean {
    return this.code === "TOO_MANY_REQUESTS";
  }

  get isUnauthorized(): boolean {
    return this.code === "UNAUTHORIZED";
  }

  /** No answer from the server at all: offline, or the request was refused (CORS, wrong address). */
  get isNetwork(): boolean {
    return this.status === 0;
  }
}

/*
 * No silent localhost fallback in a production build: prepai defaulted to localhost:3339 when the env var was
 * missing, so a misconfigured deploy quietly called the participant's own machine.
 */
const configuredUrl = import.meta.env.VITE_GRAPHQL_URL;
export const GRAPHQL_URL =
  configuredUrl ?? (import.meta.env.DEV ? "http://localhost:3340/graphql" : "");
export const API_URL = import.meta.env.VITE_API_URL ?? GRAPHQL_URL.replace(/\/graphql\/?$/, "");

const TOKEN_KEY = "gateway.token";

/**
 * The bearer token, in `localStorage`.
 *
 * Not a cookie: the API is a separate origin and drafts must replay from the offline queue. Every access is in a
 * try/catch: private mode can refuse storage, and anonymous is a valid state.
 */
export const authToken = {
  get: (): string | null => {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  },
  set: (token: string): void => {
    try {
      localStorage.setItem(TOKEN_KEY, token);
    } catch {
      /* A session that survives only in memory is better than a crash. */
    }
  },
  clear: (): void => {
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch {
      /* nothing to clear */
    }
  },
};

/*
 * Session-expiry hook. prepai had no 401 handling, so an expired token showed a generic error card on every screen.
 * The router registers a listener that clears the session and sends the person back to sign in.
 */
type Listener = () => void;
const unauthorizedListeners = new Set<Listener>();
export function onUnauthorized(listener: Listener): () => void {
  unauthorizedListeners.add(listener);
  return () => unauthorizedListeners.delete(listener);
}

interface RequestOptions {
  variables?: Record<string, unknown> | undefined;
  /** Send the bearer token. On by default: almost every operation needs it. */
  authenticated?: boolean;
  signal?: AbortSignal;
}

type GraphQLResponse<T> = {
  data?: T | null;
  errors?: { message?: string; extensions?: { code?: string } }[];
};

async function send(
  body: BodyInit,
  headers: Record<string, string>,
  authenticated: boolean,
  signal?: AbortSignal,
) {
  if (!GRAPHQL_URL) {
    throw new ApiError(
      0,
      "MISCONFIGURED",
      "This app was built without VITE_GRAPHQL_URL, so it can't reach the server.",
    );
  }
  if (authenticated) {
    const token = authToken.get();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  try {
    return await fetch(GRAPHQL_URL, {
      method: "POST",
      headers,
      body,
      ...(signal ? { signal } : {}),
    });
  } catch {
    /*
     * `fetch` throws the same TypeError for a dead network and for a request the browser blocked. `navigator.onLine`
     * separates the two well enough to point at the right thing.
     */
    const isOffline = typeof navigator !== "undefined" && navigator.onLine === false;
    if (isOffline)
      throw new ApiError(0, "OFFLINE", "You're offline. We'll retry when you're back.");
    throw new ApiError(
      0,
      "UNREACHABLE",
      "We can't reach the Readiness Engine right now. Check your connection and try again.",
    );
  }
}

async function unwrap<T>(response: Response, authenticated: boolean): Promise<T> {
  const payload = (await response.json().catch(() => null)) as GraphQLResponse<T> | null;
  const error = payload?.errors?.[0];

  if (error) {
    const code = error.extensions?.code ?? "UNKNOWN";
    const apiError = new ApiError(
      response.status,
      code,
      error.message ?? "Something went wrong. Try again.",
    );
    if (apiError.isUnauthorized && authenticated)
      unauthorizedListeners.forEach((listener) => listener());
    throw apiError;
  }
  if (!response.ok || !payload?.data) {
    throw new ApiError(response.status, "UNKNOWN", "Something went wrong. Try again.");
  }
  return payload.data;
}

export async function graphqlRequest<T>(query: string, options: RequestOptions = {}): Promise<T> {
  const { variables, authenticated = true, signal } = options;
  const response = await send(
    JSON.stringify({ query, ...(variables ? { variables } : {}) }),
    { "Content-Type": "application/json" },
    authenticated,
    signal,
  );
  return unwrap<T>(response, authenticated);
}

/**
 * A single-file upload, using the GraphQL multipart request spec the API's `graphql-upload` middleware reads.
 * `fileVariable` names the `Upload!` variable in `query`.
 */
export async function graphqlUpload<T>(
  query: string,
  variables: Record<string, unknown>,
  fileVariable: string,
  file: File,
): Promise<T> {
  const form = new FormData();
  form.append(
    "operations",
    JSON.stringify({ query, variables: { ...variables, [fileVariable]: null } }),
  );
  form.append("map", JSON.stringify({ 0: [`variables.${fileVariable}`] }));
  form.append("0", file, file.name);
  // No Content-Type: the browser sets the multipart boundary. The API's CSRF guard needs this header on uploads.
  const response = await send(form, { "Apollo-Require-Preflight": "true" }, true);
  return unwrap<T>(response, true);
}

/** Authenticated file download from a REST endpoint (CSV exports). */
export async function downloadFile(path: string, filename: string): Promise<void> {
  const token = authToken.get();
  let response: Response;
  try {
    response = await fetch(`${API_URL}${path}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  } catch {
    throw new ApiError(
      0,
      "UNREACHABLE",
      "We couldn't download that. Check your connection and try again.",
    );
  }
  if (!response.ok)
    throw new ApiError(response.status, "FORBIDDEN", "You don't have access to that export.");
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
